/**
 * Product Personalisation Handler
 * Handles adding products with personalisation add-ons to cart
 * Groups items together using _groupUID property
 *
 * Cart quantity parity (with Shopify checkout): each cart line has its own `quantity`;
 * `cart.item_count` is the sum of all line quantities. Bundle add-ons are separate lines
 * with the same quantity as the main row (updated via `updateGroupQuantity`). Plain carts
 * skip grouped-only DOM hooks so default theme qty behaviour is unchanged.
 */

(function() {
  'use strict';

  window.ProductPersonalisation = {
    // Helper function to extract property value from cart item properties
    // Handles both array format [{name: 'key', value: 'value'}] and object format {key: 'value'}
    getPropertyValue: function(properties, key) {
      if (!properties) return '';
      
      if (Array.isArray(properties)) {
        for (const prop of properties) {
          const propName = prop.name || prop.first;
          if (propName === key) {
            return prop.value || prop.last || '';
          }
        }
      } else if (typeof properties === 'object') {
        return properties[key] || '';
      }
      
      return '';
    },

    // Helper function to get item metadata from cart item
    getItemMetadata: function(item) {
      const groupUID = this.getPropertyValue(item.properties, '_groupUID');
      const isMain = this.getPropertyValue(item.properties, '_isMainItem') === 'true';
      const isAddon = this.getPropertyValue(item.properties, '_isAddon') === 'true';
      
      return { groupUID, isMain, isAddon };
    },

    /**
     * Returns true when the cart JSON contains any personalisation bundle line
     * (`_isMainItem` or `_isAddon` with `_groupUID`). Used so sort / orphan cleanup /
     * delegated cart listeners only run for those carts — plain carts stay on the default
     * theme cart path and quantities stay aligned with Shopify checkout per line key.
     *
     * @param {{ items?: unknown[] }} cart - Payload from `/cart.js`
     * @returns {boolean}
     */
    cartHasGroupedPersonalisation: function(cart) {
      if (!cart || !Array.isArray(cart.items)) {
        return false;
      }
      return cart.items.some(function(lineItem) {
        const meta = ProductPersonalisation.getItemMetadata(lineItem);
        return Boolean(meta.groupUID) && (meta.isMain || meta.isAddon);
      });
    },

    /**
     * After AJAX cart markup is injected (`cart:build`), attach grouped-cart listeners and
     * prune orphans only when `/cart.js` contains bundle lines. We intentionally do **not**
     * reorder cart rows in JS — DOM order must stay identical to `cart.items` so
     * `updates[]` / checkout quantity mapping stays correct.
     */
    runGroupedCartHooksAfterBuild: async function() {
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        if (!this.cartHasGroupedPersonalisation(cart)) {
          return;
        }
        this.initCartRemoval();
        await this.cleanupOrphanedAddons(cart);
      } catch (err) {
        await this.cleanupOrphanedAddons();
      }
    },

    init: function() {
      const form = document.querySelector('.product-single__form');
      if (!form || form.classList.contains('personalisation-loaded')) return;

      // Check if personalisation is enabled by looking for addon containers
      const hasPersonalisation = document.querySelector('.product-addon-container-gm') || 
                                 document.querySelector('.product-academy-personalisation-container-gm');
      
      if (!hasPersonalisation) return; // Don't intercept if no personalisation

      form.classList.add('personalisation-loaded');

      // Initialize state
      this.initState();
      this.bindEvents();
      this.bindFormSubmit(form);
    },

    initState: function() {
      window.personalisationState = {
        player: '',
        badge: 'no',
        sponsor: 'no',
        sponsor_back_bottom: 'no',
        naam: '',
        nummer: '',
        initials: ''
      };
    },

    bindEvents: function() {
      // Player selection
      document.querySelectorAll('.player-select')?.forEach(select => {
        select.addEventListener('change', () => {
          window.personalisationState.player = select.value.trim();
        });
      });

      // Name input
      document.querySelector('.naam-input')?.addEventListener('input', e => {
        window.personalisationState.naam = e.target.value.trim();
      });

      // Number input
      document.querySelector('.nummer-input')?.addEventListener('input', e => {
        window.personalisationState.nummer = e.target.value.trim();
      });

      // Initials toggle buttons (for academy)
      document.querySelectorAll('.initials-toggle-buttons .product-academy-personalisation-button')?.forEach(button => {
        button.addEventListener('click', () => {
          document.querySelectorAll('.initials-toggle-buttons .product-academy-personalisation-button').forEach(btn => btn.classList.remove('selected'));
          button.classList.add('selected');
          
          const value = button.getAttribute('data-value');
          const initialsInputSection = document.querySelector('.product-academy-personalisation-initials-input');
          const initialsInput = document.getElementById('initials');
          
          if (value === 'yes') {
            initialsInputSection?.classList.remove('hidden');
          } else {
            initialsInputSection?.classList.add('hidden');
            if (initialsInput) initialsInput.value = '';
            window.personalisationState.initials = '';
          }
        });
      });

      // Initials input (for academy) - with validation
      const initialsInput = document.getElementById('initials');
      if (initialsInput) {
        const initialsInputSection = document.querySelector('.product-academy-personalisation-initials-input');
        const accept = initialsInputSection?.dataset.accept; // "digits" or "letters"
        
        initialsInput.addEventListener('input', e => {
          // Validate input based on accept type
          if (accept === 'digits') {
            e.target.value = e.target.value.replace(/[^0-9]/g, '');
          } else if (accept === 'letters') {
            e.target.value = e.target.value.replace(/[^a-zA-Z]/g, '');
          }
          
          window.personalisationState.initials = e.target.value.trim();
        });

        // Handle paste events
        initialsInput.addEventListener('paste', function(e) {
          e.preventDefault();
          const pasted = (e.clipboardData || window.clipboardData).getData('text');
          const cleaned = accept === 'digits'
            ? pasted.replace(/[^0-9]/g, '')
            : pasted.replace(/[^a-zA-Z]/g, '');
          this.value = cleaned;
          window.personalisationState.initials = cleaned.trim();
        });
      }

      // Badge/Sponsor/Sponsor-back-bottom toggle buttons
      document.querySelectorAll('.product-addon-buttons')?.forEach(buttonGroup => {
        buttonGroup.addEventListener('click', event => {
          const clicked = event.target;
          if (!clicked.classList.contains('product-addon-button')) return;

          const value = clicked.getAttribute('data-value');
          buttonGroup.querySelectorAll('.product-addon-button').forEach(btn => btn.classList.remove('selected'));
          clicked.classList.add('selected');
          buttonGroup.setAttribute('data-selected', value);

          // Skip if it's initials toggle (handled separately above)
          if (buttonGroup.classList.contains('initials-toggle-buttons')) return;

          const optionType = buttonGroup.getAttribute('data-option') || 'badge';
          window.personalisationState[optionType] = value;
        });
      });

      // Personalisation mode toggle buttons
      document.querySelectorAll('.personalisation-button')?.forEach(button => {
        button.addEventListener('click', () => {
          document.querySelectorAll('.personalisation-button').forEach(btn => btn.classList.remove('selected'));
          button.classList.add('selected');

          const target = button.getAttribute('data-target');
          document.querySelector('.product-addon-player-item')?.classList.add('hidden');
          document.querySelector('.product-addon-naamnummer-container')?.classList.add('hidden');

          if (target === 'player') {
            document.querySelector('.product-addon-player-item')?.classList.remove('hidden');
          } else if (target === 'naam-nummer') {
            document.querySelector('.product-addon-naamnummer-container')?.classList.remove('hidden');
          }
        });
      });
    },

    bindFormSubmit: function(form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const addToCartBtn = form.querySelector('[data-add-to-cart]');
        if (!addToCartBtn) return false;

        addToCartBtn.disabled = true;
        addToCartBtn.innerHTML = 'Toevoegen...';

        try {
          await this.addItemsToCart(form, addToCartBtn);
        } catch (err) {
          console.error('❌ Fout bij toevoegen:', err);
          alert('Er is iets mis gegaan bij het toevoegen van de items.');
        } finally {
          addToCartBtn.disabled = false;
          addToCartBtn.innerHTML = 'Toevoegen aan winkelwagen';
        }
        
        return false;
      }, true); // Use capture phase to ensure we catch it first
    },

    getMainProductDetails: function() {
      const mainProductTitle = document.querySelector('[data-product-title]')?.getAttribute('data-product-title') || 'Onbekend Shirt';
      const variantId = parseInt(document.querySelector('select[name="id"]')?.value);
      const selectedVariant = window.meta?.product?.variants?.find(v => v.id === variantId);

      return {
        model: mainProductTitle,
        size: selectedVariant?.public_title || selectedVariant?.name || 'Unknown'
      };
    },

    generateGroupUID: function() {
      return `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    },

    addItemsToCart: async function(form, addToCartBtn) {
      // Clean up any orphaned addons before adding new items
      await this.cleanupOrphanedAddons();

      // Detect active mode
      const activeMode = document.querySelector('.personalisation-button.selected')
                        ?.getAttribute('data-target') || 'none';

      // Get current values
      const player = document.querySelector('.player-select')?.value.trim() || '';
      const naam = document.querySelector('.naam-input')?.value.trim() || '';
      const nummer = document.querySelector('.nummer-input')?.value.trim() || '';
      const initials = document.getElementById('initials')?.value.trim() || '';

      const badge = document.querySelector('#badge-product-addon .product-addon-buttons')
        ?.getAttribute('data-selected') || 'no';

      const sponsor = document.querySelector('#sponsor-sleeve-product-addon .product-addon-buttons')
        ?.getAttribute('data-selected') || 'no';

      const sponsorBackBottom = document.querySelector('#sponsor-back-bottom-product-addon .product-addon-buttons')
        ?.getAttribute('data-selected') || 'no';

      const initialsToggle = document.querySelector('.initials-toggle-buttons .product-academy-personalisation-button.selected')?.dataset?.value;

      // Validate initials if required
      if (initialsToggle === 'yes' && !initials) {
        alert('Vul je initialen in om verder te gaan.');
        return;
      }

      // Clear hidden field values
      if (activeMode === 'player') {
        const naamEl = document.querySelector('.naam-input');
        const nummerEl = document.querySelector('.nummer-input');
        if (naamEl) naamEl.value = '';
        if (nummerEl) nummerEl.value = '';
      } else if (activeMode === 'naam-nummer') {
        const playerSel = document.querySelector('.player-select');
        if (playerSel) playerSel.value = '';
      }

      const productDetails = this.getMainProductDetails();
      const shirtVariantId = form.querySelector('select[name="id"]')?.value;

      if (!shirtVariantId) {
        throw new Error('No variant selected');
      }

      // Generate unique group ID for this set of items
      const groupUID = this.generateGroupUID();
      const itemsToAdd = [];

      // Collect all personalisation properties for the main shirt
      const mainItemProperties = {
        Model: productDetails.model,
        Size: productDetails.size,
        _groupUID: groupUID,
        _isMainItem: 'true'
      };

      // Add personalisation properties based on active mode
      if (activeMode === 'player' && player) {
        mainItemProperties.Speler = player;
      } else if (activeMode === 'naam-nummer') {
        if (naam) mainItemProperties.Naam = naam;
        if (nummer) mainItemProperties.Nummer = nummer;
      }

      // Add badge and sponsor to main item properties
      if (badge === 'yes') {
        mainItemProperties.Badge = 'Eredivisie Badge';
      }
      if (sponsor === 'yes') {
        mainItemProperties.Sponsor = 'Sponsor Mouw';
      }
      if (sponsorBackBottom === 'yes') {
        mainItemProperties['Sponsor Back Bottom'] = 'Sponsor Achterkant Onder';
      }
      // Add initials property to main item (for academy personalisation)
      if (initialsToggle === 'yes' && initials) {
        // Determine property name based on product type (U21 uses "Nummer", others use "Initialen")
        const initialsInputSection = document.querySelector('.product-academy-personalisation-initials-input');
        const accept = initialsInputSection?.dataset.accept;
        const propertyName = accept === 'digits' ? 'Nummer' : 'Initialen';
        mainItemProperties[propertyName] = initials;
      }

      // Add main product with all properties
      itemsToAdd.push({
        id: parseInt(shirtVariantId),
        quantity: 1,
        properties: mainItemProperties
      });

      // Add badge add-on (if selected)
      if (badge === 'yes') {
        const badgeProductId = document.getElementById('badge-product-addon')?.dataset?.productId;
        if (badgeProductId) {
          itemsToAdd.push({
            id: parseInt(badgeProductId),
            quantity: 1,
            properties: {
              _groupUID: groupUID,
              _isAddon: 'true',
              _addonType: 'badge'
            }
          });
        }
      }

      // Add sponsor sleeve add-on (if selected)
      if (sponsor === 'yes') {
        const sponsorProductId = document.getElementById('sponsor-sleeve-product-addon')?.dataset?.productId;
        if (sponsorProductId) {
          itemsToAdd.push({
            id: parseInt(sponsorProductId),
            quantity: 1,
            properties: {
              _groupUID: groupUID,
              _isAddon: 'true',
              _addonType: 'sponsor'
            }
          });
        }
      }

      // Add sponsor back bottom add-on (if selected)
      if (sponsorBackBottom === 'yes') {
        const sponsorBackBottomProductId = document.getElementById('sponsor-back-bottom-product-addon')?.dataset?.productId;
        if (sponsorBackBottomProductId) {
          itemsToAdd.push({
            id: parseInt(sponsorBackBottomProductId),
            quantity: 1,
            properties: {
              _groupUID: groupUID,
              _isAddon: 'true',
              _addonType: 'sponsor_back_bottom'
            }
          });
        }
      }

      // Add personalisation add-ons based on active mode
      if (activeMode === 'player' && player) {
        const playerProductId = document.querySelector('.product-addon-player-item')?.dataset?.productId;
        if (playerProductId) {
          itemsToAdd.push({
            id: parseInt(playerProductId),
            quantity: 1,
            properties: {
              _groupUID: groupUID,
              _isAddon: 'true',
              _addonType: 'player'
            }
          });
        }
      } else if (activeMode === 'naam-nummer') {
        if (naam) {
          const naamProductId = document.querySelector('.naam-input')?.dataset?.productId;
          if (naamProductId) {
            itemsToAdd.push({
              id: parseInt(naamProductId),
              quantity: 1,
              properties: {
                _groupUID: groupUID,
                _isAddon: 'true',
                _addonType: 'naam'
              }
            });
          }
        }
        if (nummer) {
          const nummerProductId = document.querySelector('.nummer-input')?.dataset?.productId;
          if (nummerProductId) {
            itemsToAdd.push({
              id: parseInt(nummerProductId),
              quantity: 1,
              properties: {
                _groupUID: groupUID,
                _isAddon: 'true',
                _addonType: 'nummer'
              }
            });
          }
        }
      }

      // Add academy initials add-on (if present)
      if (initialsToggle === 'yes' && initials) {
        const initialsProductId = document.querySelector('.product-academy-personalisation-initials-input')?.dataset?.productId;
        if (initialsProductId) {
          itemsToAdd.push({
            id: parseInt(initialsProductId),
            quantity: 1,
            properties: {
              _groupUID: groupUID,
              _isAddon: 'true',
              _addonType: 'initials'
            }
          });
        }
      }

      // Add all items to cart
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsToAdd })
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.description || 'Cart addition failed');
      }

      // Open cart drawer and refresh
      this.openCartDrawerAndRefresh();
    },

    openCartDrawerAndRefresh: function() {
      const cartButton = document.querySelector('.js-drawer-open-cart');
      if (cartButton) {
        cartButton.click();
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
        }, 1500);
      } else {
        window.location.href = '/cart';
      }
    },

    initCartRemoval: function() {
      // Use event delegation to handle dynamically loaded cart items
      // Remove old listeners first
      const existingHandler = document._cartRemoveHandler;
      if (existingHandler) {
        document.removeEventListener('click', existingHandler, true);
      }

      const existingQtyHandler = document._cartQtyHandler;
      if (existingQtyHandler) {
        document.removeEventListener('click', existingQtyHandler, true);
      }

      const existingQtyInputHandler = document._cartQtyInputHandler;
      if (existingQtyInputHandler) {
        document.removeEventListener('change', existingQtyInputHandler, true);
      }

      const existingQtyEventHandler = document._cartQtyEventHandler;
      if (existingQtyEventHandler) {
        document.removeEventListener('cart:quantity.cart-cart-drawer', existingQtyEventHandler, true);
        document.removeEventListener('cart:quantity.cart-header', existingQtyEventHandler, true);
      }

      // Create handler for remove links
      document._cartRemoveHandler = async (e) => {
        // Check if clicked element is a remove link
        const link = e.target.closest('.cart__remove-link');
        if (!link) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const itemKey = link.getAttribute('data-item-key');
        const groupUID = link.getAttribute('data-group-uid');

        if (!itemKey) return;

        // If it's a grouped item, remove all items in the group
        if (groupUID) {
          try {
            // First, get current cart to find all items with this groupUID
            const cartResponse = await fetch('/cart.js');
            const cart = await cartResponse.json();

            // Find all items with the same groupUID
            const itemsToRemove = cart.items.filter(item => {
              const metadata = ProductPersonalisation.getItemMetadata(item);
              return metadata.groupUID === groupUID;
            });

            if (itemsToRemove.length === 0) {
              // Fallback to normal removal
              window.location.href = link.href;
              return;
            }

            // Remove all items in the group
            await ProductPersonalisation.removeGroupItems(groupUID);

            // Refresh cart - if on cart page, reload; otherwise refresh drawer
            if (window.location.pathname.includes('/cart')) {
              // Force a hard reload to ensure checkout sees the updated cart
              window.location.href = window.location.href.split('?')[0];
            } else {
              // For drawer, trigger cart rebuild
              document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
              // Also trigger cart:updated event
              document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
              // Wait a bit then re-init removal handlers for the new content
              setTimeout(() => {
                ProductPersonalisation.initCartRemoval();
              }, 300);
            }
          } catch (err) {
            // Fallback to normal removal on error
            window.location.href = link.href;
          }
        } else {
          // Normal removal for ungrouped items
          window.location.href = link.href;
        }
      };

      // Create handler for quantity buttons (+ and -)
      document._cartQtyHandler = async (e) => {
        // Check if clicked element is a quantity adjust button
        const adjustButton = e.target.closest('.js-qty__adjust--minus, .js-qty__adjust--plus');
        if (!adjustButton) return;

        // Find the quantity input and wrapper
        const wrapper = adjustButton.closest('.js-qty__wrapper');
        if (!wrapper) return;

        const input = wrapper.querySelector('.js-qty__num');
        if (!input) return;

        const itemKey = input.getAttribute('data-id');
        if (!itemKey) return;

        // Check if this is a grouped item
        const groupUID = input.getAttribute('data-group-uid') || adjustButton.getAttribute('data-group-uid');
        
        if (!groupUID) {
          // Not a grouped item, let default behavior handle it
          return;
        }

        // Find the cart item to verify it's a main item
        const cartItem = document.querySelector(`[data-key="${itemKey}"]`);
        if (!cartItem) return;

        const isMain = cartItem.classList.contains('cart__item--main');
        if (!isMain) return;

        // Prevent default behavior for grouped items
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        // Get current quantity
        const currentQty = parseInt(input.value) || 1;
        const isMinus = adjustButton.classList.contains('js-qty__adjust--minus');
        const isPlus = adjustButton.classList.contains('js-qty__adjust--plus');

        let newQty;
        if (isMinus) {
          // If clicking minus would result in 0, remove the group
          if (currentQty === 1) {
            await ProductPersonalisation.removeGroupItems(groupUID);
            return;
          }
          newQty = currentQty - 1;
        } else if (isPlus) {
          newQty = currentQty + 1;
        } else {
          return;
        }

        // Update all items in the group to the new quantity
        await ProductPersonalisation.updateGroupQuantity(groupUID, newQty);
      };

      // Create handler for quantity input changes
      document._cartQtyInputHandler = async (e) => {
        const input = e.target;
        if (!input || !input.classList.contains('js-qty__num')) return;

        const itemKey = input.getAttribute('data-id');
        if (!itemKey) return;

        const groupUID = input.getAttribute('data-group-uid');
        if (!groupUID) {
          // Not a grouped item, let default behavior handle it
          return;
        }

        // Find the cart item to verify it's a main item
        const cartItem = document.querySelector(`[data-key="${itemKey}"]`);
        if (!cartItem) return;

        const isMain = cartItem.classList.contains('cart__item--main');
        if (!isMain) return;

        // Get the new quantity from input
        const newQty = parseInt(input.value) || 1;

        // If quantity is 0, remove the group
        if (newQty === 0) {
          e.preventDefault();
          e.stopPropagation();
          await ProductPersonalisation.removeGroupItems(groupUID);
          return;
        }

        // Prevent default and update all items in the group
        e.preventDefault();
        e.stopPropagation();
        await ProductPersonalisation.updateGroupQuantity(groupUID, newQty);
      };

      // Create handler for cart:quantity events (dispatched by QtySelector)
      document._cartQtyEventHandler = async (e) => {
        const detail = e.detail;
        if (!detail || !Array.isArray(detail) || detail.length < 2) return;

        const itemKey = detail[0];
        const newQty = detail[1];

        if (!itemKey || !newQty) return;

        // Find the cart item
        const cartItem = document.querySelector(`[data-key="${itemKey}"]`);
        if (!cartItem) return;

        const groupUID = cartItem.getAttribute('data-group-uid');
        if (!groupUID) return;

        const isMain = cartItem.classList.contains('cart__item--main');
        if (!isMain) return;

        // Prevent default behavior for grouped items
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        // If quantity is 0, remove the group
        if (newQty === 0) {
          await ProductPersonalisation.removeGroupItems(groupUID);
          return;
        }

        // Update all items in the group
        await ProductPersonalisation.updateGroupQuantity(groupUID, newQty);
      };

      // Attach event listeners with capture phase to catch before default behavior
      document.addEventListener('click', document._cartRemoveHandler, true);
      document.addEventListener('click', document._cartQtyHandler, true);
      document.addEventListener('change', document._cartQtyInputHandler, true);
      
      // Listen for cart:quantity events (for both drawer and header)
      document.addEventListener('cart:quantity.cart-cart-drawer', document._cartQtyEventHandler, true);
      document.addEventListener('cart:quantity.cart-header', document._cartQtyEventHandler, true);
    },

    clearCart: async function() {
      // Clear entire cart - useful for testing
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        // Remove all items
        const removePromises = cart.items.map(item => {
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: item.key,
              quantity: 0
            })
          });
        });

        await Promise.all(removePromises);
        
        // Refresh
        if (window.location.pathname.includes('/cart')) {
          window.location.reload();
        } else {
          document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
        }
        
      } catch (err) {
        alert('Error clearing cart. Please try again.');
      }
    },

    updateGroupQuantity: async function(groupUID, newQty) {
      // Helper function to update quantity of all items in a group
      try {
        // Get current cart
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        // Find all items with the same groupUID
        const itemsToUpdate = cart.items.filter(item => {
          const metadata = ProductPersonalisation.getItemMetadata(item);
          return metadata.groupUID === groupUID;
        });

        if (itemsToUpdate.length === 0) return;

        // Update all items sequentially
        for (const item of itemsToUpdate) {
          try {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: item.key,
                quantity: newQty
              })
            });
            await new Promise(resolve => setTimeout(resolve, 100));
          } catch (err) {
            // Silently continue - error logged but don't block other updates
          }
        }

        // Wait for Shopify to process
        await new Promise(resolve => setTimeout(resolve, 800));

        // Refresh cart
        if (window.location.pathname.includes('/cart')) {
          window.location.href = window.location.href.split('?')[0];
        } else {
          document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
          setTimeout(() => {
            ProductPersonalisation.initCartRemoval();
          }, 300);
        }
      } catch (err) {
        // Error handled silently - cart will refresh naturally
      }
    },

    removeGroupItems: async function(groupUID) {
      // Helper function to remove all items in a group
      try {
        // Get current cart
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        // Find all items with the same groupUID
        const itemsToRemove = cart.items.filter(item => {
          const metadata = ProductPersonalisation.getItemMetadata(item);
          return metadata.groupUID === groupUID;
        });

        if (itemsToRemove.length === 0) return;

        // Remove all items sequentially
        for (const item of itemsToRemove) {
          try {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: item.key,
                quantity: 0
              })
            });
            await new Promise(resolve => setTimeout(resolve, 100));
          } catch (err) {
            // Silently continue - error logged but don't block other removals
          }
        }

        // Wait for Shopify to process
        await new Promise(resolve => setTimeout(resolve, 800));

        // Refresh cart
        if (window.location.pathname.includes('/cart')) {
          window.location.href = window.location.href.split('?')[0];
        } else {
          document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
          setTimeout(() => {
            ProductPersonalisation.initCartRemoval();
          }, 300);
        }
      } catch (err) {
        // Error handled silently - cart will refresh naturally
      }
    },

    /**
     * Removes addon lines whose `_groupUID` no longer has a main item. Skips entirely
     * when the cart has no personalisation bundle lines so plain carts never hit `/cart.js`
     * for this path (callers with a fresh cart object may pass it to avoid a second fetch).
     *
     * @param {Record<string, unknown>|undefined} cartFromCaller - Optional `/cart.js` payload
     */
    cleanupOrphanedAddons: async function(cartFromCaller) {
      // Remove addons that don't have a matching main item
      try {
        const cart = cartFromCaller
          ? cartFromCaller
          : await (await fetch('/cart.js')).json();

        if (!this.cartHasGroupedPersonalisation(cart)) {
          return;
        }

        const groupUIDs = new Set();

        // Find all main items and their groupUIDs
        cart.items.forEach(item => {
          const metadata = ProductPersonalisation.getItemMetadata(item);
          if (metadata.isMain && metadata.groupUID) {
            groupUIDs.add(metadata.groupUID);
          }
        });

        // Find orphaned addons (addons without a main item)
        const orphanedAddons = cart.items.filter(item => {
          const metadata = ProductPersonalisation.getItemMetadata(item);
          return metadata.isAddon && metadata.groupUID && !groupUIDs.has(metadata.groupUID);
        });

        // Remove orphaned addons
        if (orphanedAddons.length > 0) {
          const removePromises = orphanedAddons.map(item => {
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: item.key,
                quantity: 0
              })
            });
          });

          await Promise.all(removePromises);
          
          // Wait a bit for Shopify to process
          await new Promise(resolve => setTimeout(resolve, 300));
          
          if (window.location.pathname.includes('/cart')) {
            window.location.reload();
          } else {
            document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
          }
        }
      } catch (err) {
        // Error handled silently
      }
    }
  };

    // Initialize on DOM ready
    document.addEventListener('DOMContentLoaded', () => {
      ProductPersonalisation.init();

      // Grouped personalisation only: attach delegated cart listeners and prune orphans.
      setTimeout(async function() {
        try {
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();
          if (!ProductPersonalisation.cartHasGroupedPersonalisation(cart)) {
            return;
          }
          ProductPersonalisation.initCartRemoval();
          await ProductPersonalisation.cleanupOrphanedAddons(cart);
        } catch (err) {
          // Non-blocking: plain cart UX must not depend on this request.
        }
      }, 500);

      // Add clear cart function to window for testing (can be called from console)
      window.clearCart = () => {
        if (confirm('Are you sure you want to clear the entire cart? This cannot be undone.')) {
          ProductPersonalisation.clearCart();
        }
      };

    // Re-initialize on Shopify section reloads
    document.addEventListener('shopify:section:load', () => {
      setTimeout(async function() {
        ProductPersonalisation.init();
        try {
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();
          if (!ProductPersonalisation.cartHasGroupedPersonalisation(cart)) {
            return;
          }
          ProductPersonalisation.initCartRemoval();
        } catch (err) {
          // Ignore — section may load outside cart context.
        }
      }, 100);
    });

    // Sort cart items when cart is built (grouped lines only)
    document.addEventListener('cart:build', () => {
      setTimeout(function() {
        ProductPersonalisation.runGroupedCartHooksAfterBuild().catch(function() {
          // Swallow: cart UI already reflects server state from the build that fired this hook.
        });
      }, 200);
    });

  });

})();
